import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faCog, faComment, faTools, faFlag, faSave } from '@fortawesome/free-solid-svg-icons';
import * as yaml from 'js-yaml';
import '../styles/BotConfig.css';
import ApiService from '../utils/ApiService'; // Corrected import path

interface Tool {
  enabled: boolean;
  description?: string;
  project_name?: string;
}

// Added interfaces for CLAIbot fetching
interface CLAIBot {
  id: string;
  bot_name: string;
  // Add other relevant properties if your API returns them, e.g., project_name
}

interface CLAIBotListResponse {
  chatbots: CLAIBot[]; // Assuming API returns list under 'chatbots' key
  total: number;
}

interface ConfigurationFile {
  id: string;
  original_filename: string;
  filename?: string; // For backward compatibility
  gcs_uri: string;
  file_size: number;
  content_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BotConfigData {
  bot_name: string;
  project_name: string;
  config_name: string;
  app: {
    page_title: string;
    width: string;
  };
  model: string;
  use_agent: boolean;
  help_questions: boolean; // Changed from string[]
  only_main_profile: boolean;
  trace: boolean;
  initial_messages: {
    initial_msg: string;
    chatbox_msg: string;
  };
  tools: {
    [key: string]: Tool;
  };
  website_definition?: string;
  chatbot_goal?: string;
  avatars: {
    avatar: string;
    user_avatar: string;
    helper_avatar: string;
  };
  theme: string;
  youtube_search: boolean;
}

const defaultConfig: BotConfigData = {
  bot_name: "new_clai",
  project_name: "new_clai_project",
  config_name: "Default Configuration",
  app: {
    page_title: "New Clai Project",
    width: "wide"
  },
  model: "gpt-4.1",
  use_agent: true,
  help_questions: false, // Changed from []
  only_main_profile: false,
  trace: false,
  initial_messages: {
    initial_msg: "Hello! I'm your AI assistant. How can I help you today?",
    chatbox_msg: "Ask me anything..."
  },
  tools: {
    internet_search: { 
      enabled: true, 
      description: 'Search the internet for up-to-date information.'
    },
    avoid_foundation_model_questions: { 
      enabled: true, 
      description: 'Detect and address questions about AI foundation models.'
    },
    help_close_chatbot: { 
      enabled: true, 
      description: 'Provide help when a user wants to end the conversation.'
    },
    get_data_from_url: { 
      enabled: true, 
      description: 'Extract data from a provided URL.'
    },
    get_youtube_transcript: { 
      enabled: true, 
      description: 'Get the transcript of a YouTube video.'
    },
    youtube_search: { 
      enabled: true, 
      description: 'Search for YouTube videos related to a topic.'
    },
    local_articles_retrieval: { 
      enabled: false, 
      description: 'Retrieve articles from the local database.'
    },
    context_retrieval: { 
      enabled: true, 
      description: 'Retrieve contextual information from the knowledge base.'
    },
    librarian: { 
      enabled: false, 
      description: 'Access the librarian tool for comprehensive information retrieval.',
      project_name: ''
    }
  },
  website_definition: '',
  chatbot_goal: '',
  avatars: {
    avatar: "avatar_blue_bg.gif",
    user_avatar: ":material/person:",
    helper_avatar: ":material/info:"
  },
  theme: "light",
  youtube_search: true
};

const BotConfig: React.FC = () => {
  const [config, setConfig] = useState<BotConfigData>(defaultConfig);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // State for CLAIbot selection
  const [claiBots, setClaiBots] = useState<CLAIBot[]>([]);
  const [selectedCLAIBotId, setSelectedCLAIBotId] = useState<string | null>(null);
  const [isLoadingCLAIBots, setIsLoadingCLAIBots] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for configuration management
  const [configurations, setConfigurations] = useState<ConfigurationFile[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState<boolean>(false);
  const [showNewConfigInput, setShowNewConfigInput] = useState<boolean>(false);
  const [newConfigName, setNewConfigName] = useState<string>('');

  // Helper function to show success messages
  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Fetch CLAIbots on component mount
  useEffect(() => {
    const fetchCLAIBots = async () => {
      setIsLoadingCLAIBots(true);
      setFetchError(null);
      try {
        const response: CLAIBotListResponse = await ApiService.listChatbots(); 
        setClaiBots(response.chatbots);
      } catch (err: any) {
        setFetchError(err.message || 'Failed to fetch CLAIbots.');
      }
      setIsLoadingCLAIBots(false);
    };

    fetchCLAIBots();
  }, []);

  // Fetch configurations function
  const fetchConfigurations = useCallback(async () => {
    if (!selectedCLAIBotId) return;
    
    setIsLoadingConfigs(true);
    try {
      const configs = await ApiService.listConfigurations(selectedCLAIBotId);
      setConfigurations(configs);
    } catch (err: any) {
      console.error('Failed to fetch configurations:', err);
      // Fallback to empty array if API call fails
      setConfigurations([]);
    }
    setIsLoadingConfigs(false);
  }, [selectedCLAIBotId]);

  // Fetch configurations when a CLAIbot is selected
  useEffect(() => {
    if (selectedCLAIBotId) {
      fetchConfigurations();
    } else {
      setConfigurations([]);
      setSelectedConfigId(null);
    }
  }, [selectedCLAIBotId, fetchConfigurations]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCLAIBotId) return;

    // Validate file type
    if (!file.name.endsWith('.yaml') && !file.name.endsWith('.yml')) {
      alert('Please select a YAML file (.yaml or .yml)');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Pass the selected configuration ID if available (for updating existing configs)
      await ApiService.uploadConfiguration(selectedCLAIBotId, formData, selectedConfigId || undefined);
      
      // Parse the file locally to update the current config
      const text = await file.text();
      const parsedConfig = yaml.load(text) as BotConfigData;
      
      if (parsedConfig) {
        setConfig(parsedConfig);
        
        // Show appropriate success message based on whether we're updating or creating
        const message = selectedConfigId 
          ? `Configuration updated successfully with ${file.name}!`
          : `Configuration uploaded successfully from ${file.name}!`;
        showSuccessMessage(message);
        
        // Refresh configurations list
        fetchConfigurations();
      }
    } catch (err: any) {
      alert(`Failed to upload configuration: ${err.message}`);
    }
  };

  const saveConfiguration = async () => {
    if (!selectedCLAIBotId) return;

    if (!config.config_name || !config.config_name.trim()) {
      alert('Please enter a configuration name before saving.');
      return;
    }

    try {
      // Convert config to YAML
      const configYAML = yaml.dump(config, {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        quotingType: '"',
        forceQuotes: false
      });

      // Create a blob and form data for upload
      const blob = new Blob([configYAML], { type: 'application/x-yaml' });
      const filename = `${config.config_name.trim().replace(/[^a-zA-Z0-9_-]/g, '_')}.yaml`;
      const file = new File([blob], filename, { type: 'application/x-yaml' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Pass the selected configuration ID if available (for updating existing configs)
      await ApiService.uploadConfiguration(selectedCLAIBotId, formData, selectedConfigId || undefined);
      
      // Show appropriate success message based on whether we're updating or creating
      const message = selectedConfigId 
        ? 'Configuration updated successfully!'
        : 'Configuration saved successfully!';
      showSuccessMessage(message);
      
      fetchConfigurations(); // Refresh the list
    } catch (err: any) {
      console.error('Save configuration error:', err);
      alert(`Failed to save configuration: ${err.message || 'Unknown error'}`);
    }
  };

  const loadConfiguration = async (configId: string) => {
    if (!selectedCLAIBotId) return;

    try {
      const configResponse = await ApiService.loadConfigurationContent(selectedCLAIBotId, configId);
      
      // Parse the YAML content
      const parsedConfig = yaml.load(configResponse.content) as BotConfigData;
      
      if (parsedConfig) {
        setConfig(parsedConfig);
        setSelectedConfigId(configId);
        showSuccessMessage(`Configuration "${configResponse.filename}" loaded successfully!`);
      } else {
        throw new Error('Invalid configuration format');
      }
    } catch (err: any) {
      console.error('Failed to load configuration:', err);
      showSuccessMessage(`Failed to load configuration: ${err.message}`);
      // Reset selected config on error
      setSelectedConfigId(null);
    }
  };

  const handleConfigurationSelect = (value: string) => {
    if (value === 'create_new') {
      setShowNewConfigInput(true);
      setSelectedConfigId(null);
      // Reset to default config for new configuration
      const selectedBot = claiBots.find(bot => bot.id === selectedCLAIBotId);
      if (selectedBot) {
        setConfig({
          ...defaultConfig,
          bot_name: selectedBot.bot_name,
          project_name: `${selectedBot.bot_name}_project`,
          config_name: ''
        });
      }
    } else if (value) {
      loadConfiguration(value);
      setShowNewConfigInput(false);
    } else {
      setSelectedConfigId(null);
      setShowNewConfigInput(false);
    }
  };

  const handleNewConfigNameSave = () => {
    if (newConfigName.trim()) {
      setConfig(prev => ({
        ...prev,
        config_name: newConfigName.trim()
      }));
      setShowNewConfigInput(false);
      setNewConfigName('');
      showSuccessMessage(`New configuration "${newConfigName.trim()}" created successfully!`);
    }
  };

  const handleNewConfigCancel = () => {
    setShowNewConfigInput(false);
    setNewConfigName('');
  };

  const handleInputChange = (field: keyof BotConfigData, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleToolToggle = (toolName: string) => {
    setConfig(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        [toolName]: {
          ...prev.tools[toolName],
          enabled: !prev.tools[toolName].enabled
        }
      }
    }));
  };

  const handleToolDescriptionChange = (toolName: string, description: string) => {
    setConfig(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        [toolName]: {
          ...prev.tools[toolName],
          description
        }
      }
    }));
  };

  const handleLibrarianProjectChange = (projectName: string) => {
    setConfig(prev => ({
      ...prev,
      tools: {
        ...prev.tools,
        librarian: {
          ...prev.tools.librarian,
          project_name: projectName
        }
      }
    }));
  };

  // Handle CLAIbot selection
  const handleCLAIBotSelect = (botId: string) => {
    if (botId === "") {
      setSelectedCLAIBotId(null);
      // Reset to empty state when no bot is selected
      setConfig(defaultConfig);
    } else {
      const selectedBot = claiBots.find(bot => bot.id === botId);
      setSelectedCLAIBotId(botId);
      if (selectedBot) {
        setConfig(prev => ({
          ...prev,
          bot_name: selectedBot.bot_name,
          // You might want to derive project_name or use a specific field if available
          project_name: `${selectedBot.bot_name}_project` 
        }));
      }
    }
  };

  const downloadConfig = () => {
    // Convert config object to YAML format using js-yaml
    const configYAML = yaml.dump(config, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      quotingType: '"',
      forceQuotes: false
    });
    const blob = new Blob([configYAML], { type: 'application/x-yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.bot_name}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Show success message
    showSuccessMessage('Configuration downloaded successfully!');
  };

  const formatToolName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="bot-config">
      {/* Basic Information Section */}
      <div className="config-section">
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faCog} /> Basic Information</h3>
        </div>
        <p className="form-info">Configure the core identity of your chatbot.</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}> {/* Wrapper for form fields */}
          {/* CLAIbot Selector */}
          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ fontWeight: 'bold' }}>Select CLAIbot *</label>
            <div className="input-group">
              <select
                value={selectedCLAIBotId || ''}
                onChange={(e) => handleCLAIBotSelect(e.target.value)}
                disabled={isLoadingCLAIBots}
                required
                style={{
                  borderColor: '#D4804D',
                  borderWidth: '2px',
                  // borderRadius: '5px',
                  boxShadow: '0 0 0 2px rgba(212, 128, 77, 0.1)',
                  backgroundColor: selectedCLAIBotId ? '#fff' : '#faf7f4'
                }}
              >
                <option value="">-- Select a CLAIbot --</option>
                {claiBots.map(bot => (
                  <option key={bot.id} value={bot.id}>
                    {bot.bot_name}
                  </option>
                ))}
              </select>
              {isLoadingCLAIBots && <p>Loading CLAIbots...</p>}
              {fetchError && <p style={{ color: 'red' }}>Error: {fetchError}</p>}
              {!selectedCLAIBotId && !isLoadingCLAIBots && (
                <p style={{ color: 'orange', fontSize: '12px', margin: '5px 0 0 0' }}>
                  Please select a CLAIbot to configure tools and settings
                </p>
              )}
            </div>
          </div>

          {/* Configuration Management Section */}
          {selectedCLAIBotId && (
            <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
              <label className="textarea-label">Load Configuration</label>
              <div className="input-group">
                <select
                  value={selectedConfigId || ''}
                  onChange={(e) => handleConfigurationSelect(e.target.value)}
                  disabled={isLoadingConfigs}
                >
                  <option value="">-- Select a configuration --</option>
                  <option value="create_new" style={{ fontWeight: 'bold', color: '#D4804D' }}>+ Create New Configuration</option>
                  {configurations.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.original_filename || config.filename} {config.is_active ? '(Active)' : ''}
                    </option>
                  ))}
                </select>
                {isLoadingConfigs && <p style={{ fontSize: '12px', color: '#666' }}>Loading configurations...</p>}
                
                {/* New Configuration Input */}
                {showNewConfigInput && (
                  <div style={{ marginTop: '10px' }}>
                    <input
                      type="text"
                      placeholder="Enter new configuration name"
                      value={newConfigName}
                      onChange={(e) => setNewConfigName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #D4804D',
                        borderRadius: '4px',
                        marginBottom: '5px'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleNewConfigNameSave();
                        }
                      }}
                    />
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={handleNewConfigNameSave}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#D4804D',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleNewConfigCancel}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#ccc',
                          color: 'black',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* YAML Upload Section */}
          {selectedCLAIBotId && (
            <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
              <label className="textarea-label">Upload YAML Configuration</label>
              <div className="input-group">
                <input
                  type="file"
                  accept=".yaml,.yml"
                  onChange={handleFileUpload}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
            </div>
          )}
          
          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Bot Name
            </label>
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter bot name"
                value={config.bot_name}
                onChange={(e) => handleInputChange('bot_name', e.target.value)}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              />
            </div>
          </div>
        
          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Project Name
            </label>
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter project name"
                value={config.project_name}
                onChange={(e) => handleInputChange('project_name', e.target.value)}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              />
            </div>
          </div>
        
          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Page Title
            </label>
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter page title"
                value={config.app.page_title}
                onChange={(e) => handleInputChange('app', {
                  ...config.app,
                  page_title: e.target.value
                })}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              />
            </div>
          </div>
        
          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Model
            </label>
            <div className="input-group">
              <select
                value={config.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              >
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4.1">GPT-4.1</option>
              </select>
            </div>
          </div>

          <div className="form-field" style={{ flex: '0 1 calc((100% - 30px) / 3)' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Theme
            </label>
            <div className="input-group">
              <select
                value={config.theme}
                onChange={(e) => handleInputChange('theme', e.target.value)}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div>
          </div>
        </div> {/* End of wrapper */}
      </div>

      {/* Feature Flags Section */}
      <div className="config-section" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faFlag} /> Feature Flags</h3>
        </div>
        <p className="form-info">Toggle advanced features for your chatbot.</p>
        
        <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
          <label style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
            <input
              type="checkbox"
              checked={config.use_agent}
              onChange={(e) => handleInputChange('use_agent', e.target.checked)}
              disabled={!selectedCLAIBotId}
            />
            Use Agent
          </label>
          
          <label style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
            <input
              type="checkbox"
              checked={config.only_main_profile}
              onChange={(e) => handleInputChange('only_main_profile', e.target.checked)}
              disabled={!selectedCLAIBotId}
            />
            Only Main Profile
          </label>
          
          <label style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
            <input
              type="checkbox"
              checked={config.trace}
              onChange={(e) => handleInputChange('trace', e.target.checked)}
              disabled={!selectedCLAIBotId}
            />
            Enable Trace
          </label>

          <label style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
            <input
              type="checkbox"
              checked={config.help_questions}
              onChange={(e) => handleInputChange('help_questions', e.target.checked)}
              disabled={!selectedCLAIBotId}
            />
            Enable Help Questions
          </label>
        </div>
      </div>

      {/* Messages Section */}
      <div className="config-section" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faComment} /> Messages</h3>
        </div>
        <p className="form-info">Configure the help questions and initial messages your bot will use.</p>
        
        <div className="form-field">
          <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
            Initial Greeting Message
          </label>
          <textarea
            placeholder="Enter initial greeting message"
            value={config.initial_messages.initial_msg}
            onChange={(e) => handleInputChange('initial_messages', {
              ...config.initial_messages,
              initial_msg: e.target.value
            })}
            disabled={!selectedCLAIBotId}
            style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
          />
        </div>

        <div className="form-field">
          <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
            Chatbox Placeholder
          </label>
          <textarea
            placeholder="Enter chatbox placeholder text"
            value={config.initial_messages.chatbox_msg}
            onChange={(e) => handleInputChange('initial_messages', {
              ...config.initial_messages,
              chatbox_msg: e.target.value
            })}
            disabled={!selectedCLAIBotId}
            style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
          />
        </div>
      </div>

      {/* Tools Section */}
      <div className="config-section" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faTools} /> Tools</h3>
        </div>
        <p className="form-info">Enable or disable tools that your bot can use to assist users.</p>
        
        {!selectedCLAIBotId && (
          <p style={{ color: 'orange', fontSize: '14px', margin: '10px 0' }}>
            Please select a CLAIbot to configure tools
          </p>
        )}
        
        <div className="tools-grid">
          {Object.entries(config.tools).map(([toolName, toolConfig]) => (
            <div key={toolName} className="tool-card" style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
              <div className="tool-header">
                <label style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}>
                  <input
                    type="checkbox"
                    checked={toolConfig.enabled}
                    onChange={() => handleToolToggle(toolName)}
                    disabled={!selectedCLAIBotId}
                  />
                  {formatToolName(toolName)}
                </label>
              </div>
              
              <div className="form-field">
                <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
                  Tool Description
                </label>
                <textarea
                  placeholder="Enter tool description"
                  value={toolConfig.description || ''}
                  onChange={(e) => handleToolDescriptionChange(toolName, e.target.value)}
                  disabled={!selectedCLAIBotId}
                  style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
                />
              </div>
              
              {toolName === 'librarian' && toolConfig.enabled && (
                <div className="form-field">
                  <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
                    Project Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter librarian project name"
                    value={toolConfig.project_name || ''}
                    onChange={(e) => handleLibrarianProjectChange(e.target.value)}
                    disabled={!selectedCLAIBotId}
                    style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Avatar Selection Section */}
      <div className="config-section" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faCog} /> Avatar Selection</h3>
        </div>
        <p className="form-info">Choose an avatar for your chatbot.</p>

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <div className="form-field" style={{ flex: 1, marginRight: '10px' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Bot Avatar
            </label>
            <div className="input-group">
              <select
                value={config.avatars.avatar}
                onChange={(e) => handleInputChange('avatars', { 
                  ...config.avatars, 
                  avatar: e.target.value 
                })}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              >
                <option value="avatar_blue_bg.gif">Blue Background</option>
                <option value="avatar_orange.gif">Orange Background</option>
                <option value="avatar_green.gif">Green Background</option>
                <option value=":material/person:">Default Person</option>
                <option value=":material/info:">Info Icon</option>
              </select>
            </div>
          </div>
          
          <div className="form-field" style={{ flex: 1, marginRight: '10px' }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              User Avatar
            </label>
            <div className="input-group">
              <select
                value={config.avatars.user_avatar}
                onChange={(e) => handleInputChange('avatars', { 
                  ...config.avatars, 
                  user_avatar: e.target.value 
                })}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              >
                <option value=":material/person:">Person Icon</option>
                <option value=":material/account:">Account Icon</option>
                <option value=":material/face:">Face Icon</option>
              </select>
            </div>
          </div>
          
          <div className="form-field" style={{ flex: 1 }}>
            <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
              Helper Avatar
            </label>
            <div className="input-group">
              <select
                value={config.avatars.helper_avatar}
                onChange={(e) => handleInputChange('avatars', { 
                  ...config.avatars, 
                  helper_avatar: e.target.value 
                })}
                disabled={!selectedCLAIBotId}
                style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
              >
                <option value=":material/info:">Info Icon</option>
                <option value=":material/help:">Help Icon</option>
                <option value=":material/support:">Support Icon</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metadata Section */}
      <div className="config-section" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
        <div className="section-title-container">
          <h3><FontAwesomeIcon icon={faCog} /> Additional Metadata</h3>
        </div>
        <p className="form-info">Provide additional context for your chatbot.</p>

        <div className="form-field">
          <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
            Website Definition
          </label>
          <textarea
            placeholder="Describe your website or application."
            value={config.website_definition || ''}
            onChange={(e) => handleInputChange('website_definition', e.target.value)}
            disabled={!selectedCLAIBotId}
            style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
          />
        </div>

        <div className="form-field">
          <label className="textarea-label" style={{ opacity: !selectedCLAIBotId ? 0.5 : 1 }}>
            Chatbot Goal
          </label>
          <textarea
            placeholder="Define the main purpose of your bot."
            value={config.chatbot_goal || ''}
            onChange={(e) => handleInputChange('chatbot_goal', e.target.value)}
            disabled={!selectedCLAIBotId}
            style={{ opacity: !selectedCLAIBotId ? 0.6 : 1 }}
          />
        </div>
      </div>

      {/* Save/Download Buttons */}
      <div className="botconfig-buttons-container">
        <button 
          onClick={downloadConfig} 
          className="botconfig-button botconfig-download-button"
          disabled={!selectedCLAIBotId}
          style={{ 
            opacity: !selectedCLAIBotId ? 0.5 : 1,
            cursor: !selectedCLAIBotId ? 'not-allowed' : 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Download Configuration
        </button>
        <button 
          onClick={saveConfiguration} 
          className="botconfig-button botconfig-save-button"
          disabled={!selectedCLAIBotId}
          style={{ 
            opacity: !selectedCLAIBotId ? 0.5 : 1,
            cursor: !selectedCLAIBotId ? 'not-allowed' : 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faSave} /> Save Configuration
        </button>
      </div>
      
      {/* Success Message */}
      {showSuccess && (
        <div className="floating-notification">
          <FontAwesomeIcon icon={faSave} />
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default BotConfig;